import { Icon } from '@iconify/react';
import { Box, Stack, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import Button from '../../../components/custom/Button';
import { FundLogo } from '../../../components/custom/FundLogo';
import Select from '../../../components/custom/Select';
import Tabs from '../../../components/custom/Tabs';
import TextField from '../../../components/custom/TextField';
import { useAuth } from '../../../utils/context/useAuthContext';
import File from './File';
import Link from './Link';

interface FormValues {
  fullName: string;
  clientId: string;
  amount: string;
  description?: string;
  bank?: string;
  branch?: string;
  account?: string;
  polisaNumber?: string;
}

interface BankOption {
  label: string;
  value: string;
}

interface BranchOption {
  label: string;
  value: string;
}

const apiClient = generateClient<Schema>();

const GenerateLink = () => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({});

  const formData = watch();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const indexLogo = searchParams.get('indexLogo') || '';
  const product = searchParams.get('product') || '';
  const fund = searchParams.get('fund') || '';

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [agentId, setAgentId] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [banks, setBanks] = useState<BankOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);

  const selectedBank = watch('bank');

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await apiClient.queries.getBanksInfo({});

        let banksList: BankOption[] = [];

        if (response?.data) {
          try {
            const data =
              typeof response.data === 'string'
                ? JSON.parse(response.data)
                : response.data;

            if (data && data.body && Array.isArray(data.body)) {
              banksList = data.body
                .map((bank: any) => ({
                  label: bank.name || '',
                  value: bank.number ? String(bank.number) : '',
                }))
                .filter((bank: BankOption) => bank.label && bank.value);
            }
          } catch (e) {
            console.error('Error processing bank data:', e);
          }
        }

        setBanks(banksList);
      } catch (error) {
        console.error('Error fetching banks:', error);
        setBanks([]);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    const fetchBranchesAndReset = async () => {
      setValue('branch', '', {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });

      if (!selectedBank) {
        setBranches([]);
        return;
      }

      try {
        const response = await apiClient.queries.getBanksInfo({
          bank: selectedBank,
        });

        let branchesList: BranchOption[] = [];

        if (response?.data) {
          try {
            const data =
              typeof response.data === 'string'
                ? JSON.parse(response.data)
                : response.data;

            if (data && data.body && Array.isArray(data.body)) {
              branchesList = data.body
                .map((branch: any) => ({
                  label: branch.name
                    ? `${branch.branch || ''} - ${branch.name}`
                    : branch.branch || '',
                  value: branch.branch || '',
                }))
                .filter((branch: BranchOption) => branch.value);
            }
          } catch (e) {
            console.error('Error processing branch data:', e);
          }
        }

        setBranches(branchesList);
      } catch (error) {
        console.error('Error fetching branches:', error);
        setBranches([]);
      }
    };

    fetchBranchesAndReset();
  }, [selectedBank, setValue]);

  const myTabs = [
    { label: 'פוליסה חדשה', content: 'פוליסה חדשה' },
    { label: 'פוליסה קיימת', content: 'פוליסה קיימת' },
  ];

  const { user } = useAuth();
  const userId = user?.userId;

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    if (index === 0) {
      setValue('polisaNumber', '');
    }
  };

  useEffect(() => {
    const fetchAgent = async () => {
      if (!userId) return;

      try {
        const response = await apiClient.models.User.get({ id: userId });
        if (response?.data?.agentId) {
          setAgentId(response.data.agentId);
        } else {
          enqueueSnackbar('לא נמצאו פרטי סוכן, אנא פנה לתמיכה', {
            variant: 'error',
          });
        }
      } catch (error) {
        enqueueSnackbar(
          'אירעה שגיאה בטעינת פרטי הסוכן, אנא נסה שוב מאוחר יותר',
          { variant: 'error' },
        );
      }
    };

    fetchAgent();
  }, [userId, enqueueSnackbar]);

  const validateForm = (data: FormValues): boolean => {
    let isValid = true;

    if (activeTab === 1 && !data.polisaNumber) {
      enqueueSnackbar('יש להזין מספר פוליסה', { variant: 'error' });
      isValid = false;
    }

    if (!data.fullName) {
      enqueueSnackbar('יש להזין שם לקוח', { variant: 'error' });
      isValid = false;
    }

    if (!data.clientId) {
      enqueueSnackbar('יש להזין תעודת זהות', { variant: 'error' });
      isValid = false;
    }

    if (!data.amount) {
      enqueueSnackbar('יש להזין סכום', { variant: 'error' });
      isValid = false;
    }

    return isValid;
  };

  const handleSubmitFunction = handleSubmit(async (data: FormValues) => {
    if (!validateForm(data)) return;

    try {
      setLoading(true);

      if (!fund) {
        throw new Error('missing_fund');
      }

      if (!agentId) {
        throw new Error('missing_agent');
      }

      const response = await apiClient.queries.generateAgentPaymentLink({
        clientId: data.clientId,
        fund: fund,
        agentId: agentId,
        userId: userId || '',
        clientBank: data.bank || '',
        clientBranch: data.branch || '',
        clientAccount: data.account || '',
        clientFullName: data.fullName,
        paymentAmount: data.amount,
        polisaNumber: data.polisaNumber || '',
        description: data.description || '',
      });

      if (!response?.data) {
        throw new Error('no_data');
      }

      const parsedData =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

      if (!parsedData?.body || typeof parsedData.body !== 'object') {
        throw new Error('invalid_format');
      }

      const { paymentLink, paymentAmount } = parsedData.body;

      if (!paymentLink) {
        throw new Error('missing_link');
      }

      setPaymentLink(paymentLink);
      setAmount(Number(paymentAmount) ?? null);
      setLinkOpen(true);
    } catch (error) {
      const errorMessage = (error as Error).message;

      const errorMessages: Record<string, string> = {
        missing_fund: 'לא נמצאו פרטי קרן, אנא פנה לתמיכה',
        missing_agent: 'לא נמצאו פרטי סוכן, אנא פנה לתמיכה',
        no_data: 'לא התקבלו נתונים מהשרת, אנא נסה שוב',
        invalid_format: 'התקבלו נתונים לא תקינים מהשרת, אנא פנה לתמיכה',
        missing_link: 'לא התקבל קישור לתשלום, אנא נסה שוב',
      };

      const userMessage =
        errorMessages[errorMessage] ||
        'אירעה שגיאה ביצירת קישור לתשלום, אנא נסה שוב מאוחר יותר';
      enqueueSnackbar(userMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  });

  const handleFileUpload = (selectedFile: File) => {
    setFile(selectedFile);
    setFileOpen(false);
  };

  return (
    <>
      <Stack
        spacing={5}
        component="form"
        onSubmit={handleSubmitFunction}
        noValidate
      >
        <Stack direction="row-reverse" alignItems="center" spacing={2}>
          <FundLogo logoIndex={indexLogo} width="170px" height="60px" />
          <Icon icon="iconamoon:arrow-left-2-bold" width="30px" />
          <Typography fontSize="32px" fontWeight="700" fontFamily={'AlmoniDL'}>
            {product}
          </Typography>
        </Stack>
        <Stack direction="row-reverse" spacing={35}>
          <Stack spacing={4} width="400px">
            <Typography
              textAlign="right"
              fontSize="32px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              פרטי לקוח
            </Typography>
            <Controller
              name="fullName"
              control={control}
              rules={{
                required: 'חובה למלא את השדה',
                validate: (value) => {
                  if (!/^[a-zA-Z\u0590-\u05FF\s]+$/.test(value)) {
                    return 'יש להזין רק אותיות בעברית ובאנגלית';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="שם לקוח"
                  fullWidth
                  required
                  error={!!errors.fullName}
                  value={field.value?.toString() || ''}
                  helperText={errors.fullName?.message}
                />
              )}
            />
            <Controller
              name="clientId"
              control={control}
              rules={{
                required: 'חובה למלא את השדה',
                validate: (value) => {
                  if (!/^\d{9}$/.test(value)) {
                    return 'תעודת זהות חייבת להכיל 9 ספרות בדיוק';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="תעודת זהות"
                  fullWidth
                  required
                  error={!!errors.clientId}
                  value={field.value?.toString() || ''}
                  helperText={errors.clientId?.message}
                />
              )}
            />
            <Controller
              name="amount"
              control={control}
              rules={{
                required: 'חובה למלא את השדה',
                validate: (value) => {
                  if (!/^\d+$/.test(value)) {
                    return 'יש להזין רק ספרות';
                  }
                  const amountValue = parseInt(value, 10);
                  if (amountValue <= 0) {
                    return 'הסכום חייב להיות גדול מ-0';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="הזן סכום"
                  fullWidth
                  customVariant="money"
                  required
                  error={!!errors.amount}
                  value={field.value?.toString() || ''}
                  helperText={errors.amount?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="הערות"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={3}
                  error={!!errors.description}
                  value={field.value?.toString() || ''}
                  helperText={errors.description?.message}
                />
              )}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    background: '#B6F829',
                    width: '48px',
                    height: '48px',
                    borderRadius: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => setFileOpen(true)}
                >
                  <Icon width="26px" icon="qlementine-icons:plus-16" />
                </Box>
                <Typography
                  fontSize="20px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  העלאת קובץ
                </Typography>
              </Stack>
              {file && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#00000042',
                      py: '5px',
                      px: '15px',
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'center',
                      borderRadius: '30px',
                      width: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Typography
                      fontWeight={'700'}
                      fontSize={'14px'}
                      fontFamily={'AlmoniDL'}
                      color={'#FFF'}
                    >
                      {file.name}
                    </Typography>
                    <Icon
                      style={{ cursor: 'pointer' }}
                      icon={'material-symbols:close'}
                      color="#FFF"
                      onClick={() => setFile(null)}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </Stack>
          <Stack spacing={4} width="400px">
            <Typography
              textAlign="right"
              fontSize="32px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              חשבון בנק לקוח
            </Typography>
            <Controller
              name="bank"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  value={field.value || ''}
                  label="בחר בנק"
                  options={banks}
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  id="bank-select"
                />
              )}
            />
            <Controller
              name="branch"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  value={field.value || ''}
                  label="בחר סניף"
                  options={branches}
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  id="branch-select"
                />
              )}
            />
            <Controller
              name="account"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="מספר חשבון"
                  fullWidth
                  error={!!errors.account}
                  value={field.value?.toString() || ''}
                  helperText={errors.account?.message}
                />
              )}
            />

            <Stack spacing={2}>
              <Stack spacing={1.5}>
                <Tabs
                  tabs={myTabs}
                  initialTab={0}
                  onChange={handleTabChange}
                  contrast={false}
                />

                <Controller
                  name="polisaNumber"
                  control={control}
                  rules={
                    activeTab === 1 ? { required: 'חובה למלא את השדה' } : {}
                  }
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="מספר פוליסה"
                      fullWidth
                      required={activeTab === 1}
                      error={activeTab === 1 && !!errors.polisaNumber}
                      helperText={
                        activeTab === 1 ? errors.polisaNumber?.message : ' '
                      }
                      sx={{
                        visibility: activeTab === 1 ? 'visible' : 'hidden',

                        '& .MuiFormHelperText-root': {
                          minHeight: '1.4375em',
                        },
                      }}
                      aria-hidden={activeTab !== 1}
                    />
                  )}
                />
              </Stack>

              <Button loading={loading} type="submit">
                צור לינק תשלום
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {paymentLink && amount !== null && (
        <Link
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
          indexLogo={indexLogo}
          paymentLink={paymentLink}
          amount={amount}
          clientName={formData.fullName || ''}
        />
      )}
      <File
        open={fileOpen}
        onClose={() => setFileOpen(false)}
        setSelectedFile={handleFileUpload}
      />
    </>
  );
};

export default GenerateLink;
